import type { CSSProperties, InputHTMLAttributes, ReactNode } from 'react';
import { c, trackedBlock } from '../theme';
import { LoopingVideo } from './LoopingVideo';

/**
 * The shared system behind Onboarding, Sign Up, Sign In and Buyer Fit (the
 * auth-flow handoff): the looping clip behind a scrim, frosted pill fields
 * and the violet call to action. Built once here, used by all four screens.
 */

export const BRAND_GRADIENT = 'linear-gradient(150deg,#6A2BD9 0%,#D63A2E 40%,#E22A8C 70%,#2BB3E6 100%)';
/** Links on the dark scrim. */
export const VIOLET_LIGHT = '#A374F0';
export const STRENGTH_GREEN = '#1E8A5A';

const SCRIM = {
  dark: 'linear-gradient(180deg, rgba(31,26,31,0.75) 0%, rgba(31,26,31,0.5) 30%, rgba(31,26,31,0.85) 62%, rgba(31,26,31,0.96) 100%)',
  light:
    'radial-gradient(70% 55% at 85% 15%, rgba(106,43,217,0.45) 0%, rgba(106,43,217,0) 70%), radial-gradient(60% 45% at 10% 70%, rgba(106,43,217,0.3) 0%, rgba(106,43,217,0) 70%), linear-gradient(180deg,rgba(245,238,219,0.3) 0%,rgba(245,238,219,0.45) 50%,rgba(245,238,219,0.72) 100%)',
};

/** The 390px frame with the clip and its scrim behind everything. */
export function AuthScene({
  scrim,
  minHeight,
  children,
}: {
  scrim: 'dark' | 'light';
  minHeight?: number;
  children: ReactNode;
}) {
  return (
    <div
      className="rb-stage"
      style={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', background: c.page, padding: '40px 0 60px' }}
    >
      <div
        className="rb-shell"
        style={{
          position: 'relative',
          width: 390,
          maxWidth: '100%',
          minHeight,
          borderRadius: 40,
          overflow: 'hidden',
          background: c.ink,
          color: scrim === 'dark' ? c.cream : c.ink,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <LoopingVideo src="/onboarding-bg.mp4" fallback={BRAND_GRADIENT} />
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            background: SCRIM[scrim],
            ...(scrim === 'light' ? { backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' } : null),
          }}
        />
        <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', flex: 1 }}>{children}</div>
      </div>
    </div>
  );
}

/** "Let's get you **ready**." — 32px, a bold word, and a red full stop. */
export function SceneHeadline({ children }: { children: ReactNode }) {
  return (
    <h1 style={{ margin: 0, fontSize: 32, fontWeight: 600, lineHeight: 1.05, letterSpacing: '-0.03em', whiteSpace: 'nowrap' }}>
      {children}
      <span style={{ color: c.red }}>.</span>
    </h1>
  );
}

export function SceneLead({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <p style={{ margin: 0, fontSize: 15, lineHeight: 1.45, fontWeight: 500, color: c.ink, maxWidth: 300, textWrap: 'pretty', ...style }}>
      {children}
    </p>
  );
}

/** A 64px frosted pill holding a badge dot and the input. */
export function PillField({
  dot,
  trailing,
  inputStyle,
  ...input
}: InputHTMLAttributes<HTMLInputElement> & { dot: string; trailing?: ReactNode; inputStyle?: CSSProperties }) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        height: 64,
        padding: '0 8px 0 7px',
        borderRadius: 999,
        background: 'rgba(245,238,219,0.62)',
        border: '1px solid rgba(245,238,219,0.8)',
        boxSizing: 'border-box',
      }}
    >
      <span
        aria-hidden
        style={{ width: 50, height: 50, flex: 'none', borderRadius: '50%', background: 'rgba(245,238,219,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: dot }} />
      </span>
      <input className="rb-pill-input" {...input} style={{ flex: 1, minWidth: 0, ...inputStyle }} />
      {trailing}
    </label>
  );
}

/** The cream chip inside a pill field: FORGOT?, the password strength. */
export function PillChip({ children, bg = c.cream, fg = c.ink, onClick }: { children: ReactNode; bg?: string; fg?: string; onClick?: () => void }) {
  const style: CSSProperties = {
    flex: 'none',
    padding: '12px 14px',
    borderRadius: 999,
    background: bg,
    color: fg,
    fontSize: 9,
    letterSpacing: '0.14em',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  };
  return onClick ? (
    <button type="button" onClick={onClick} style={style}>
      {children}
    </button>
  ) : (
    <span style={style}>{children}</span>
  );
}

const Chevron = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden>
    <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** The 64px violet call to action, with its chevron. */
export function VioletCTA({
  children,
  disabled,
  type = 'button',
  onClick,
  chevron = true,
  style,
}: {
  children: ReactNode;
  disabled?: boolean;
  type?: 'button' | 'submit';
  onClick?: () => void;
  chevron?: boolean;
  style?: CSSProperties;
}) {
  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        width: '100%',
        boxSizing: 'border-box',
        height: 64,
        borderRadius: 999,
        background: disabled ? 'rgba(31,26,31,0.12)' : c.violet,
        color: disabled ? 'rgba(31,26,31,0.4)' : c.cream,
        fontSize: 12,
        fontWeight: 600,
        textTransform: 'uppercase',
        cursor: disabled ? 'default' : 'pointer',
        ...trackedBlock(0.16),
        ...style,
      }}
    >
      {children}
      {chevron && <Chevron />}
    </button>
  );
}

/** Underlined red text link: SIGN IN, CREATE AN ACCOUNT, SKIP. */
export function SceneLink({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ fontSize: 10, letterSpacing: '0.12em', fontWeight: 600, color: c.red, textDecoration: 'underline' }}
    >
      {children}
    </button>
  );
}

/** A 22px tone indicator: tone at 18%, a 30% glow, a 6px core. */
export function ToneDot({ tone, size = 22, children }: { tone: string; size?: number; children?: ReactNode }) {
  const rgba = (a: number) =>
    `rgba(${parseInt(tone.slice(1, 3), 16)},${parseInt(tone.slice(3, 5), 16)},${parseInt(tone.slice(5, 7), 16)},${a})`;
  return (
    <span
      aria-hidden
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: rgba(0.18),
        boxShadow: `0 0 10px ${rgba(0.3)}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 'none',
        fontSize: 10,
        fontWeight: 700,
        color: tone,
      }}
    >
      {children ?? <span style={{ width: 6, height: 6, borderRadius: '50%', background: tone }} />}
    </span>
  );
}
